import React, { useState } from 'react'
import { Autocomplete, Box, Button, FormControl, Grid, MenuItem, Select, TextField, Typography } from '@mui/material'
import type { SelectChangeEvent } from '@mui/material/Select'
import SearchIcon from '@mui/icons-material/Search'
import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router-dom'
import type { Location } from 'react-router-dom'
import useSWR from 'swr'
import { HeaderSearchTextCancelButton, HeaderSearchTextField } from '~/src/components/molecules'
import { otherRepository, orderRepository } from '~/src/repositories'
import type { IWishListParams, WishListStatus } from '~/src/repositories/WishList/models/WishList'

const EMPTY_SEARCH: IWishListParams = {
  companyName: '',
  productCode: '',
  displayName: '',
  makerName: '',
  carName: '',
  carModel: '',
  engineModel: '',
  stockType: '',
  stockSource: '',
  status: undefined,
}

const STATUS_FILTER_VALUES: Record<'AVAILABLE' | 'WAITING' | 'ORDER_CONFIRMED', WishListStatus> = {
  AVAILABLE: 'Available',
  WAITING: 'Waiting',
  ORDER_CONFIRMED: 'OrderConfirmed',
}

const getStatusSelectValue = (status?: WishListStatus): string => {
  if (status === STATUS_FILTER_VALUES.AVAILABLE) return 'AVAILABLE'
  if (status === STATUS_FILTER_VALUES.WAITING) return 'WAITING'
  if (status === STATUS_FILTER_VALUES.ORDER_CONFIRMED) return 'ORDER_CONFIRMED'
  return ''
}

const pickState = (state: unknown): Partial<IWishListParams> =>
  state && typeof state === 'object' ? (state as Partial<IWishListParams>) : {}

interface IWishListFormProps {
  onSearch: (values: IWishListParams) => void
}

export const WishListForm: React.FC<IWishListFormProps> = ({ onSearch }): JSX.Element => {
  const { t } = useTranslation()
  const location: Location = useLocation()
  const [formValues, setFormValues] = useState<IWishListParams>(() => ({
    ...EMPTY_SEARCH,
    ...pickState(location.state),
  }))

  const { data: products } = useSWR(['/products'], () => otherRepository.getProducts(), {
    fallbackData: [],
  })

  const { data: makers } = useSWR(['/request-orders/makers'], () => orderRepository.requestOrderMakers(), {
    fallbackData: [],
  })

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const name = e.target.name as keyof IWishListParams
    if (Object.prototype.hasOwnProperty.call(EMPTY_SEARCH, name)) {
      setFormValues({ ...formValues, [name]: e.target.value })
    }
  }

  const handleStatusChange = (e: SelectChangeEvent<string>) => {
    const value = e.target.value
    setFormValues({
      ...formValues,
      status:
        value === 'AVAILABLE'
          ? STATUS_FILTER_VALUES.AVAILABLE
          : value === 'WAITING'
            ? STATUS_FILTER_VALUES.WAITING
            : value === 'ORDER_CONFIRMED'
              ? STATUS_FILTER_VALUES.ORDER_CONFIRMED
              : undefined,
    })
  }

  const handleProductTypeChange = (e: SelectChangeEvent<string>) => {
    setFormValues({
      ...formValues,
      stockType: e.target.value,
    })
  }

  const handleStockSourceChange = (e: SelectChangeEvent<string>) => {
    setFormValues({
      ...formValues,
      stockSource: e.target.value,
    })
  }

  const handleProductCodeChange = (_event: React.SyntheticEvent, value: string | null) => {
    setFormValues({
      ...formValues,
      productCode: value ?? '',
    })
  }

  const handleMakerNameChange = (_event: React.SyntheticEvent, value: string | null) => {
    setFormValues({
      ...formValues,
      makerName: value ?? '',
    })
  }

  const handleClear = (key: keyof IWishListParams) => {
    if (key === 'status') {
      setFormValues({ ...formValues, status: undefined })
      return
    }
    setFormValues({ ...formValues, [key]: '' })
  }

  const handleClears = () => {
    onSearch({ ...EMPTY_SEARCH })
    setFormValues({ ...EMPTY_SEARCH })
  }

  const submitSearch = () => {
    onSearch({ ...formValues })
  }

  const selectFieldSx = {
    width: '220px',
    height: '40px',
    mr: '8px',
    background: 'white',
    '& .MuiOutlinedInput-input': {
      padding: '8.5px 14px',
      fontSize: 16,
      lineHeight: '24px',
    },
  }

  return (
    <Grid container sx={{ width: '100%', maxWidth: '100%', marginBottom: '24px', alignItems: 'flex-end' }}>
      <Grid item xs={12} lg sx={{ minWidth: 0 }}>
        <Grid container spacing={1} sx={{ alignItems: 'flex-end' }}>
          <Grid item>
            <HeaderSearchTextField
              name="companyName"
              value={formValues.companyName}
              onChange={handleChange}
              placeholder={t('wish_list.company_name')}
              InputProps={{
                endAdornment: <HeaderSearchTextCancelButton onClick={() => handleClear('companyName')} />,
              }}
            />
          </Grid>
          <Grid item>
            <FormControl size="small" sx={selectFieldSx}>
              <Autocomplete
                ListboxProps={{ style: { fontSize: 14 } }}
                options={products.map((product) => product.code)}
                getOptionLabel={(option) => {
                  const product = products.find(({ code, name }) => code === option)
                  return product ? `${product.code} (${product.name})` : ''
                }}
                value={formValues.productCode || null}
                onChange={handleProductCodeChange}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    size="small"
                    placeholder={t('wish_list.product')}
                    inputProps={{
                      ...params.inputProps,
                      style: { fontSize: 16 },
                    }}
                  />
                )}
              />
            </FormControl>
          </Grid>
          <Grid item>
            <HeaderSearchTextField
              name="displayName"
              value={formValues.displayName}
              onChange={handleChange}
              placeholder={t('wish_list.product_name')}
              InputProps={{
                endAdornment: <HeaderSearchTextCancelButton onClick={() => handleClear('displayName')} />,
              }}
            />
          </Grid>
          <Grid item>
            <FormControl size="small" sx={selectFieldSx}>
              <Autocomplete
                ListboxProps={{ style: { fontSize: 14 } }}
                options={makers}
                value={formValues.makerName || null}
                onChange={handleMakerNameChange}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    size="small"
                    placeholder={t('wish_list.maker_name')}
                    inputProps={{
                      ...params.inputProps,
                      style: { fontSize: 16 },
                    }}
                  />
                )}
              />
            </FormControl>
          </Grid>
          <Grid item>
            <HeaderSearchTextField
              name="carName"
              value={formValues.carName}
              onChange={handleChange}
              placeholder={t('wish_list.car_name')}
              InputProps={{
                endAdornment: <HeaderSearchTextCancelButton onClick={() => handleClear('carName')} />,
              }}
            />
          </Grid>
          <Grid item>
            <HeaderSearchTextField
              name="carModel"
              value={formValues.carModel}
              onChange={handleChange}
              placeholder={t('wish_list.car_model')}
              InputProps={{
                endAdornment: <HeaderSearchTextCancelButton onClick={() => handleClear('carModel')} />,
              }}
            />
          </Grid>
          <Grid item>
            <HeaderSearchTextField
              name="engineModel"
              value={formValues.engineModel}
              onChange={handleChange}
              placeholder={t('wish_list.engine_model')}
              InputProps={{
                endAdornment: <HeaderSearchTextCancelButton onClick={() => handleClear('engineModel')} />,
              }}
            />
          </Grid>
          <Grid item>
            <FormControl size="small" sx={selectFieldSx}>
              <Select
                displayEmpty
                value={formValues.stockType ?? ''}
                onChange={handleProductTypeChange}
                inputProps={{ 'aria-label': t('wish_list.product_type') }}
                renderValue={(value) => value || t('wish_list.product_type')}
                sx={{
                  color: (theme) =>
                    formValues.stockType ? theme.palette.text.primary : 'rgba(0, 0, 0, 0.5)',
                  fontSize: 16,
                  lineHeight: '24px',
                  fontStyle: 'normal',
                }}
              >
                <MenuItem value=""></MenuItem>
                <MenuItem value="Engine">Engine</MenuItem>
                <MenuItem value="Part">Part</MenuItem>
                <MenuItem value="CkdCar">CkdCar</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item>
            <FormControl size="small" sx={selectFieldSx}>
              <Select
                displayEmpty
                value={formValues.stockSource ?? ''}
                onChange={handleStockSourceChange}
                inputProps={{ 'aria-label': t('wish_list.stock_source') }}
                renderValue={(value) => value || t('wish_list.stock_source')}
                sx={{
                  color: (theme) =>
                    formValues.stockSource ? theme.palette.text.primary : 'rgba(0, 0, 0, 0.5)',
                  fontSize: 16,
                  lineHeight: '24px',
                  fontStyle: 'normal',
                }}
              >
                <MenuItem value=""></MenuItem>
                <MenuItem value="ePW">ePW</MenuItem>
                <MenuItem value="Auction">Auction</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item>
            <FormControl size="small" sx={selectFieldSx}>
              <Select
                displayEmpty
                value={getStatusSelectValue(formValues.status)}
                onChange={handleStatusChange}
                inputProps={{ 'aria-label': t('wish_list.status') }}
                renderValue={(value) => value || t('wish_list.status')}
                sx={{
                  color: (theme) =>
                    formValues.status !== undefined ? theme.palette.text.primary : 'rgba(0, 0, 0, 0.5)',
                  fontSize: 16,
                  lineHeight: '24px',
                  fontStyle: 'normal',
                }}
              >
                <MenuItem value=""></MenuItem>
                <MenuItem value="AVAILABLE">{t('wish_list.available')}</MenuItem>
                <MenuItem value="WAITING">{t('wish_list.waiting')}</MenuItem>
                <MenuItem value="ORDER_CONFIRMED">{t('wish_list.order_confirmed')}</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Grid>
      <Grid
        item
        xs={12}
        lg="auto"
        sx={{
          flexShrink: 0,
          display: 'flex',
          justifyContent: { xs: 'flex-start', lg: 'flex-end' },
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            flexWrap: 'nowrap',
            alignItems: 'center',
            gap: '8px',
            ml: { lg: '8px' },
          }}
        >
          <Button
            variant="outlined"
            sx={{
              flexShrink: 0,
              height: '40px',
              padding: '7px 22px',
              background: '#FFFFFF',
            }}
            onClick={handleClears}
          >
            <Typography
              sx={{
                fontStyle: 'normal',
                fontSize: 15,
                letterSpacing: 0.46,
                lineHeight: '26px',
                fontWeight: 500,
              }}
            >
              {t('wish_list.clear')}
            </Typography>
          </Button>
          <Button
            variant="contained"
            sx={{
              flexShrink: 0,
              display: 'inline-flex',
              flexDirection: 'row',
              alignItems: 'center',
              height: '40px',
              padding: '7px 22px 7px 19px',
              whiteSpace: 'nowrap',
            }}
            onClick={submitSearch}
          >
            <SearchIcon fontSize={'small'} sx={{ width: '20px', height: '20px' }} />
            <Typography
              sx={{
                marginLeft: '3px',
                fontStyle: 'normal',
                fontSize: 15,
                letterSpacing: 0.46,
                lineHeight: '26px',
                fontWeight: 500,
              }}
            >
              {t('wish_list.search')}
            </Typography>
          </Button>
        </Box>
      </Grid>
    </Grid>
  )
}
